import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button, Input, notification, Table, Tag, Tooltip, Popconfirm, Space, Spin, Select, Card } from 'antd';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import utc from 'dayjs/plugin/utc';

import { BiExport } from 'react-icons/bi';
import { FaArchive, FaEye, FaPen, FaPlus, FaSearch } from 'react-icons/fa';
import { FaBoxArchive, FaBoxOpen } from "react-icons/fa6";
import { FiFilter } from 'react-icons/fi';
import { HiOutlineRefresh } from 'react-icons/hi';

import '../../../css/UserAdmin/Global.css';

import Footer from '../Footer';
import Header from '../NewHeader';
import SideNav from '../SideNav';
import { DateRangeFilter } from '../../../utils/tableUtils';

dayjs.extend(utc);
dayjs.extend(isBetween);

const API_URL = process.env.REACT_APP_API_URL || '';

const getFacultyFullName = (faculty) => {
    if (!faculty) return 'N/A';
    const firstName = faculty.firstName || '';
    const middleName = faculty.middleName ? ` ${faculty.middleName.trim()}` : '';
    const lastName = faculty.lastName || '';
    return `${firstName}${middleName} ${lastName}`.trim() || 'Name not available';
};


const ScheduleTable = () => {
    const navigate = useNavigate();
    const [isSideNavOpen, setIsSideNavOpen] = useState(false);
    const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 768);

    const [dataSource, setDataSource] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [tableFilters, setTableFilters] = useState({});
    const [apiFilters, setApiFilters] = useState({});
    const [sorter, setSorter] = useState({});
    const [showArchived, setShowArchived] = useState(false);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });

    const [academicYears, setAcademicYears] = useState([]);
    const [selectedAcademicYear, setSelectedAcademicYear] = useState('');
    const [yearsLoading, setYearsLoading] = useState(true);
    const [yearsError, setYearsError] = useState(null);

    // State for dynamic terms
    const [academicTerms, setAcademicTerms] = useState([]);
    const [selectedAcademicTerm, setSelectedAcademicTerm] = useState('');
    const [termsLoading, setTermsLoading] = useState(false);


    const [gradeLevels, setGradeLevels] = useState([]);
    const [sectionsForFilter, setSectionsForFilter] = useState([]);


    const toggleSideNav = useCallback(() => setIsSideNavOpen(prev => !prev), []);

    useEffect(() => {
        const handleResize = () => setIsSmallScreen(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Function to fetch academic terms based on a given year
    const fetchAcademicTerms = useCallback(async (year) => {
        if (!year) {
            setAcademicTerms([]);
            setSelectedAcademicTerm('');
            return;
        }
        setTermsLoading(true);
        setAcademicTerms([]);
        setSelectedAcademicTerm('');
        try {
            const response = await fetch(`${API_URL}/api/admin/terms?academicYear=${encodeURIComponent(year)}&includeInactive=true`);
            if (!response.ok) throw new Error('Failed to fetch academic terms');
            const result = await response.json();
            const termsData = result.data || [];
            setAcademicTerms(termsData);
            const activeTerm = termsData.find(t => t.status === 'Active');
            if (activeTerm) {
                setSelectedAcademicTerm(activeTerm.term);
            } else if (termsData.length > 0) {
                // Fallback to the first term in the list if none are active
                setSelectedAcademicTerm(termsData[0].term);
            }
        } catch (error) {
            console.error(`Could not load academic terms for ${year}.`, error);
            notification.error({ message: 'Fetch Error', description: `Could not load academic terms for ${year}.` });
            setAcademicTerms([]);
        } finally {
            setTermsLoading(false);
        }
    }, []);

    // Initial fetch for academic years and other filter options
    useEffect(() => {
        const fetchInitialFilterData = async () => {
            setYearsLoading(true);
            setYearsError(null);
            setLoading(true);

            try {
                // Fetch academic years, grade levels, and sections in parallel
                const ayPromise = fetch(`${API_URL}/api/admin/years?includeArchived=true&includeInactive=true&limit=100`);
                const glPromise = fetch(`${API_URL}/api/admin/year-level?status=Active&limit=100`);
                const secPromise = fetch(`${API_URL}/api/admin/sections?status=Active&limit=1000`);

                const [ayResult, glResult, secResult] = await Promise.allSettled([ayPromise, glPromise, secPromise]);

                // Process Academic Years response
                let yearToFetchTermsFor = null;
                if (ayResult.status === 'fulfilled' && ayResult.value.ok) {
                    const ayData = await ayResult.value.json();
                    const years = ayData.data || ayData || [];
                    setAcademicYears(years);

                    const activeYear = years.find(year => year.status === 'Active');
                    if (activeYear) {
                        setSelectedAcademicYear(activeYear.Year);
                        yearToFetchTermsFor = activeYear.Year; // Set the year to fetch terms for
                    } else if (years.length > 0) {
                        setSelectedAcademicYear(years[0].Year);
                        yearToFetchTermsFor = years[0].Year; // Fallback year
                    }
                } else {
                    setYearsError('Could not load years.');
                    console.error("Failed to fetch academic years for filter", ayResult.reason || 'Request failed');
                }

                // If a year was selected, fetch its terms immediately
                if (yearToFetchTermsFor) {
                    await fetchAcademicTerms(yearToFetchTermsFor);
                }

                // Process Grade Levels response
                if (glResult.status === 'fulfilled' && glResult.value.ok) {
                    const glData = await glResult.value.json();
                    setGradeLevels(glData.data || glData || []);
                } else {
                    console.error("Failed to fetch grade levels for filter", glResult.reason || 'Request failed');
                }

                // Process Sections response
                if (secResult.status === 'fulfilled' && secResult.value.ok) {
                    const secData = await secResult.value.json();
                    const uniqueSections = [...new Set((secData.data || secData || []).map(s => s.sectionName))].sort();
                    setSectionsForFilter(uniqueSections.map(name => ({ text: name, value: name })));
                } else {
                    console.error("Failed to fetch sections for filter", secResult.reason || 'Request failed');
                }

            } catch (error) {
                console.error("A critical error occurred while fetching filter options:", error);
                setYearsError('Could not load filter options.');
                notification.error({ message: "Error", description: "Could not load initial filter options." });
            } finally {
                setYearsLoading(false);
                setLoading(false);
            }
        };

        fetchInitialFilterData();
    }, [fetchAcademicTerms]); // Add fetchAcademicTerms to dependency array

    // Effect to re-fetch terms when the academic year changes
    useEffect(() => {
        if (selectedAcademicYear && !yearsLoading) {
            fetchAcademicTerms(selectedAcademicYear);
        }
    }, [selectedAcademicYear, yearsLoading, fetchAcademicTerms]);


    const fetchClassSchedules = useCallback(async () => {
        // Prevent fetching if required filters are still loading or not selected
        if (yearsLoading || termsLoading || !selectedAcademicYear || !selectedAcademicTerm) {
            setDataSource([]);
            return;
        }

        setLoading(true);
        try {
            const params = new URLSearchParams({
                page: pagination.current,
                limit: pagination.pageSize,
                search: searchTerm,
                isArchived: String(showArchived),
                sortBy: sorter.field || 'createdAt',
                sortOrder: sorter.order === 'ascend' ? 'asc' : (sorter.order === 'descend' ? 'desc' : 'desc'),
                academicYear: selectedAcademicYear,
                academicTerm: selectedAcademicTerm, // Use the dynamic term
            });

            Object.entries(apiFilters).forEach(([key, value]) => {
                if (value) {
                    if (Array.isArray(value) && value.length > 0) {
                        value.forEach(v => params.append(key, v));
                    } else if (!Array.isArray(value)) {
                        params.append(key, value);
                    }
                }
            });

            const fullUrl = `${API_URL}/api/admin/class-schedules?${params.toString()}`;
            const response = await fetch(fullUrl);

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
            }
            const result = await response.json();

            setDataSource(result.data.map(item => ({ ...item, key: item._id })));
            setPagination(prev => ({
                ...prev,
                current: parseInt(result.pagination.currentPage, 10),
                pageSize: parseInt(result.pagination.pageSize, 10),
                total: parseInt(result.pagination.totalItems, 10),
            }));

        } catch (error) {
            console.error('Error fetching class schedules:', error);
            setDataSource([]);
            notification.error({
                message: 'Fetch Error',
                description: error.message || 'Failed to fetch class schedules.',
            });
        } finally {
            setLoading(false);
        }
    }, [pagination.current, pagination.pageSize, searchTerm, showArchived, sorter, apiFilters, selectedAcademicYear, selectedAcademicTerm, yearsLoading, termsLoading]);

    useEffect(() => {
        fetchClassSchedules();
    }, [fetchClassSchedules]);

    const handleTableChange = (newPagination, antdFilters, newSorter, extra) => {
        if (extra.action === 'paginate' || extra.action === 'sort' || extra.action === 'filter') {
            const newApiFilters = {};
            Object.keys(antdFilters).forEach(key => {
                if (antdFilters[key] && antdFilters[key].length > 0) {
                    if (key === 'createdAt' || key === 'updatedAt') {
                        newApiFilters[`${key}From`] = dayjs(antdFilters[key][0]).startOf('day').toISOString();
                        newApiFilters[`${key}To`] = dayjs(antdFilters[key][1]).endOf('day').toISOString();
                    } else {
                        newApiFilters[key] = antdFilters[key].length === 1 ? antdFilters[key][0] : antdFilters[key];
                    }
                }
            });

            setApiFilters(newApiFilters);
            setTableFilters(antdFilters);
            setSorter(newSorter.field ? { field: newSorter.field, order: newSorter.order, columnKey: newSorter.columnKey } : {});

            let pageToFetch = newPagination.current;
            if (extra.action === 'sort' || extra.action === 'filter') {
                pageToFetch = 1;
            }
            setPagination(prev => ({ ...prev, current: pageToFetch, pageSize: newPagination.pageSize }));
        }
    };


    const handleSearch = (value) => {
        setSearchTerm(value);
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    const handleClearFilters = () => {
        setTableFilters({});
        setApiFilters({});
        setSorter({});
        setSearchTerm('');
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    const handleRefresh = () => {
        fetchClassSchedules();
    };

    const handleCreate = () => navigate('/user-admin/schedule/create');

    const createLog = async (action, detail) => {
        try {
            const userID = localStorage.getItem('userID');
            const accountName = localStorage.getItem('fullName');
            const role = localStorage.getItem('role');
            if (!userID || !accountName || !role) {
                console.warn('User info not found in localStorage for logging.'); return;
            }
            const logData = { userID, accountName, role, action, detail };
            const response = await fetch(`${API_URL}/api/admin/system-logs`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(logData),
            });
            if (response.ok) {
                const data = await response.json();
                console.log("System log recorded:", data);
            } else {
                console.warn("Failed to record system log:", response.statusText);
            }
        } catch (error) {
            console.error('Failed to record system log:', error);
        }
    };

    const handleExport = () => {
        const currentDate = dayjs().format('YYYY-MM-DD');
        const reportType = "Class Schedules";
        const fileName = `${reportType.toLowerCase().replace(/\s+/g, '-')}-report-${currentDate}.pdf`;

        const adminUserID = localStorage.getItem('userID');
        const adminFullName = localStorage.getItem('fullName');
        const adminRole = localStorage.getItem('role');

        if (!adminUserID || !adminFullName || !adminRole) {
            notification.error({ message: 'Export Error', description: 'Admin user details not found.' });
            return;
        }

        const exportParams = new URLSearchParams({
            requestingUserID: adminUserID,
            requestingUserFullName: adminFullName,
            requestingUserRole: adminRole,
            search: searchTerm,
            isArchived: String(showArchived),
            sortBy: sorter.field || 'createdAt',
            sortOrder: sorter.order === 'ascend' ? 'asc' : (sorter.order === 'descend' ? 'desc' : 'desc'),
            academicYear: selectedAcademicYear,
        });

        if (selectedAcademicTerm) {
            exportParams.append('academicTerm', selectedAcademicTerm);
        }

        Object.entries(apiFilters).forEach(([key, value]) => {
            if (value) {
                if (Array.isArray(value)) {
                    value.forEach(v => exportParams.append(key, String(v)));
                } else {
                    exportParams.append(key, String(value));
                }
            }
        });

        const exportUrl = `${API_URL}/api/admin/export/class-schedules?${exportParams.toString()}`;
        console.log("Exporting Class Schedules from URL:", exportUrl);
        fetch(exportUrl, { method: 'GET' })
            .then(response => {
                if (!response.ok) {
                    return response.text().then(text => {
                        let errorMsg = `Network response was not ok (${response.status})`;
                        try { const errorData = JSON.parse(text); if (errorData && errorData.message) errorMsg = errorData.message; }
                        catch (e) { if (text) errorMsg = text; }
                        throw new Error(errorMsg);
                    });
                }
                return response.blob();
            })
            .then(blob => {
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                notification.success({
                    message: 'Export Successful',
                    description: `${fileName} has been downloaded.`,
                });

                createLog('Export', `Exported ${reportType} report: ${fileName}`);
            })
            .catch(error => {
                console.error(`${reportType} Export failed:`, error);
                notification.error({
                    message: 'Export Failed',
                    description: error.message || `There was an error exporting the ${reportType.toLowerCase()} report.`,
                });
            });
    };

    const handleToggleArchiveStatus = async (record, archiveAction) => {
        const { _id, status, section, gradeLevel, isArchived: currentArchivedStatus } = record;
        const actionVerb = archiveAction ? 'archive' : 'unarchive';

        if (archiveAction && !currentArchivedStatus && status === 'Active') {
            notification.warning({
                message: 'Action Denied',
                description: 'Cannot archive an Active class schedule. Set to Inactive first.',
            });
            return;
        }

        try {
            const response = await fetch(`${API_URL}/api/admin/class-schedules/${_id}/${actionVerb}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || `Failed to ${actionVerb} class schedule`);
            }

            notification.success({
                message: 'Action Successful',
                description: `Class schedule has been ${actionVerb}d.`
            });

            createLog(
                `${actionVerb.charAt(0).toUpperCase() + actionVerb.slice(1)} Class Schedule`,
                `${actionVerb}d class schedule (ID: ${_id}, ${gradeLevel || 'N/A'} - ${section || 'N/A'})`
            );

            fetchClassSchedules();
        } catch (error) {
            console.error(`Error ${actionVerb}ing class schedule:`, error);
            notification.error({
                message: `${actionVerb.charAt(0).toUpperCase() + actionVerb.slice(1)} Failed`,
                description: error.message || `An error occurred.`,
            });
        }
    };

    const columns = [
        {
            title: 'Section',
            dataIndex: 'section',
            key: 'section',
            width: 80,
            fixed: 'left',
            sorter: true,
            sortOrder: sorter.columnKey === 'section' ? sorter.order : null,
            filters: sectionsForFilter,
            filteredValue: tableFilters.section || null,
            filterSearch: true,
            responsive: ['lg'],
        },
        {
            title: 'Academic Year',
            dataIndex: 'academicYear',
            key: 'academicYear',
            width: 80,
            sorter: true,
            sortOrder: sorter.columnKey === 'academicYear' ? sorter.order : null,
        },
        {
            title: 'Term', dataIndex: 'academicTerm', key: 'academicTerm', width: 80,
            sorter: true, sortOrder: sorter.columnKey === 'academicTerm' ? sorter.order : null,
        },
        {
            title: 'Grade Level',
            dataIndex: 'gradeLevel',
            key: 'gradeLevel',
            width: 80,
            sorter: true,
            sortOrder: sorter.columnKey === 'gradeLevel' ? sorter.order : null,
            filters: gradeLevels.map(gl => ({ text: gl.level, value: gl.level })),
            filteredValue: tableFilters.gradeLevel || null,
            filterSearch: true,
            responsive: ['lg'],
        },
        {
            title: 'Status', dataIndex: 'status', key: 'status', width: 100,
            filters: [{ text: 'Active', value: 'Active' }, { text: 'Inactive', value: 'Inactive' }, { text: 'Pending', value: 'Pending' }],
            filteredValue: tableFilters.status || null,
            render: (status) => {
                let color;
                switch (status) {
                    case 'Active': color = 'success'; break;
                    case 'Inactive': color = 'error'; break;
                    case 'Pending': color = 'processing'; break;
                    default: color = 'default';
                }
                return <Tag color={color}>{status}</Tag>;
            },
            sorter: true, sortOrder: sorter.columnKey === 'status' ? sorter.order : null,
        },
        {
            title: 'Schedule Details', key: 'entries', width: 400,
            render: (_, record) => (
                <div style={{ maxHeight: '120px', overflowY: 'auto', fontSize: '12px', paddingRight: '5px' }}>
                    {record.scheduleEntries && record.scheduleEntries.length > 0 ? record.scheduleEntries.map((entry, index) => (
                        <div key={index} style={{ borderBottom: index < record.scheduleEntries.length - 1 ? '1px dashed #eee' : 'none', paddingBottom: '4px', marginBottom: '4px' }}>
                            <div><strong>{entry.subjectCode}</strong> - <span style={{ fontStyle: 'italic' }}>{entry.subjectName}</span></div>
                            <div style={{ color: '#444', marginLeft: '10px' }}>
                                Faculty: {getFacultyFullName(entry.facultyRef)}
                            </div>
                            <div style={{ color: '#555', marginLeft: '10px' }}>
                                Room: {entry.room} | Days: {entry.days?.join(', ')} | Time: {entry.timeStart}-{entry.timeEnd}
                            </div>
                        </div>
                    )) : <Tag>No schedule entries</Tag>}
                </div>
            ),
        },
        {
            title: 'Created At', dataIndex: 'createdAt', key: 'createdAt', width: 170, responsive: ['xl'],
            render: (text) => dayjs(text).isValid() ? dayjs.utc(text).local().format('YYYY-MM-DD HH:mm') : 'N/A',
            sorter: true, sortOrder: sorter.columnKey === 'createdAt' ? sorter.order : null,
            filterDropdown: (props) => <DateRangeFilter {...props} />,
            filteredValue: tableFilters.createdAt || null,
        },
        {
            title: 'Action', key: 'action', fixed: 'right', width: 170,
            render: (_, record) => {
                const isDisabledForArchive = !record.isArchived && (record.status === 'Active' || record.status === 'Pending');
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'center' }}>
                        <Button
                            icon={record.isArchived ? <FaEye /> : <FaPen />}
                            onClick={() => navigate(
                                record.isArchived
                                    ? `/user-admin/schedule/view/${record._id}`
                                    : `/user-admin/schedule/edit/${record._id}`
                            )}
                            style={{ width: '150px', display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}
                        >
                            {record.isArchived ? 'View Details' : 'Edit Schedule'}
                        </Button>
                        <Popconfirm
                            title={`Are you sure to ${record.isArchived ? 'unarchive' : 'archive'} this schedule?`}
                            onConfirm={() => handleToggleArchiveStatus(record, !record.isArchived)}
                            okText="Yes"
                            cancelText="No"
                            disabled={isDisabledForArchive}
                        >
                            <Tooltip
                                title={
                                    isDisabledForArchive
                                        ? 'Schedule must be Inactive to archive.'
                                        : (record.isArchived ? 'Restore Schedule' : 'Archive Schedule')
                                }
                            >
                                <Button
                                    icon={record.isArchived ? <FaBoxOpen /> : <FaArchive />}
                                    disabled={isDisabledForArchive}
                                    danger={!record.isArchived}
                                    style={{ width: '150px', display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}
                                >
                                    {record.isArchived ? 'Unarchive' : 'Archive'}
                                </Button>
                            </Tooltip>
                        </Popconfirm>
                    </div>
                );
            }
        }
    ];

    return (
        <div className={`admin-page-container ${isSideNavOpen ? 'admin-sidenav-open' : ''}`}>
            <Header onMenuClick={toggleSideNav} isSideNavOpen={isSideNavOpen} />
            {showArchived && (
                <Tag className="archived-tag" color="volcano">Viewing <strong>Archived Class Schedules</strong></Tag>
            )}
            <div className="admin-content-wrapper">
                <div className="admin-sidenav-container">
                    <SideNav isOpen={isSideNavOpen} onClose={toggleSideNav} isSmallScreen={isSmallScreen} />
                </div>
                <main className="admin-main-content-area">
                    <div className="page-title">
                        <p className="heading">{showArchived ? "Archived Class Schedules" : "Manage Class Schedules"}</p>
                    </div>

                    <Card
                        title="Filter Options"
                        size="small"
                        style={{ marginBottom: '16px', marginTop: '16px', borderRadius: '6px', border: '1px solid #dedede' }}
                        styles={{
                            header: {
                                backgroundColor: '#00245A',
                                color: '#ffffff',
                            }
                        }}
                    >
                        <Space align="center" wrap>
                            <Space align="center">
                                <label style={{ fontWeight: 500 }}>Academic Year:</label>
                                <Select
                                    style={{ minWidth: 150 }}
                                    value={selectedAcademicYear}
                                    onChange={(value) => {
                                        setSelectedAcademicYear(value);
                                        setPagination(prev => ({ ...prev, current: 1 }));
                                    }}
                                    loading={yearsLoading}
                                    disabled={yearsLoading || !!yearsError}
                                >
                                    {yearsError ? (
                                        <Select.Option disabled value="">{yearsError}</Select.Option>
                                    ) : (
                                        academicYears.map(year => (
                                            <Select.Option key={year._id} value={year.Year}>
                                                {year.Year} {year.status === 'Active' && '(Active)'}
                                            </Select.Option>
                                        ))
                                    )}
                                </Select>
                            </Space>
                            <Space align="center">
                                <label style={{ fontWeight: 500 }}>Academic Term:</label>
                                <Select
                                    style={{ minWidth: 150 }}
                                    value={selectedAcademicTerm || undefined}
                                    onChange={(value) => {
                                        setSelectedAcademicTerm(value);
                                        setPagination(prev => ({ ...prev, current: 1 }));
                                    }}
                                    loading={termsLoading}
                                    disabled={!selectedAcademicYear || termsLoading || yearsLoading}
                                    placeholder="Select Term"
                                >
                                    {academicTerms.map(term => (
                                        <Select.Option key={term._id} value={term.term}>
                                            {term.term} {term.status === 'Active' && '(Active)'}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Space>
                        </Space>
                    </Card>

                    <div className="table-functions">
                        <Space className="left-tools" wrap>
                            <Button icon={<FiFilter />} onClick={handleClearFilters}>Clear Filters</Button>
                            <Button icon={<HiOutlineRefresh />} onClick={handleRefresh} loading={loading}>Refresh</Button>
                            <Button icon={<BiExport />} onClick={handleExport}>Export</Button>
                        </Space>
                        <Space className="right-tools" wrap>
                            <Input
                                placeholder="Search (Section, Faculty, Subject...)"
                                allowClear
                                style={{ width: 280 }}
                                value={searchTerm}
                                onChange={(e) => handleSearch(e.target.value)}
                                onPressEnter={() => fetchClassSchedules()}
                                suffix={<FaSearch style={{ color: '#aaa' }} />}
                            />
                            <Button
                                icon={showArchived ? <FaBoxOpen /> : <FaBoxArchive />}
                                onClick={() => {
                                    setShowArchived(!showArchived);
                                    setPagination(prev => ({ ...prev, current: 1 }));
                                }}
                            >
                                {showArchived ? 'Active Schedules' : 'View Archive'}
                            </Button>
                            {!showArchived && (
                                <Button type="primary" className="create-btn" icon={<FaPlus />} onClick={handleCreate}>Create Schedule</Button>
                            )}
                        </Space>
                    </div>

                    <Spin spinning={loading}>
                        <Table
                            className="class-schedule-table"
                            columns={columns}
                            dataSource={dataSource}
                            scroll={{ x: 1900 }}
                            pagination={{
                                ...pagination,
                                showSizeChanger: true,
                                showQuickJumper: true,
                                pageSizeOptions: ['10', '20', '50', '100'],
                                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                            }}
                            bordered
                            onChange={handleTableChange}
                            rowKey="key"
                            size="middle"
                        />
                    </Spin>
                </main>
            </div>
            <Footer />
        </div>
    );
};

export default ScheduleTable;